import {useEffect, useState} from 'react';
import {Alert, Button, Center, Checkbox, Dialog, Field, Input, InputGroup, Portal, Spinner, VStack} from '@chakra-ui/react';
import {DialogCloseButton} from './ui/dialog-close-button';
import {toaster} from './ui/toaster';
import {useJsonFile, useSaveEventInfo} from '../drive/queries';
import {
    getCoverFileName,
    INVALID_EVENT_INFO_MESSAGE,
    isSameEventInfo,
    normalizeEventInfo,
    parseEventInfoContentFile,
    writeCoverFileName,
} from '../domain/eventInfo';
import {JsonFileContent} from '../domain/json';
import {useEventSuggestions, usePhotographerSuggestions} from '../suggestions/useSuggestions';
import {TagListInput} from './TagListInput';
import {describeDriveError} from '../drive/driveApi';
import { isPictureFileName } from '../domain/naming';

// v2's invalid input: a light red border with a matching 1px ring.
const INVALID_INPUT = {borderColor: 'red.300', boxShadow: '0 0 0 1px var(--chakra-colors-red-300)'};

interface EventInfoEditorProps {
    isOpen: boolean;
    onClose: () => void;
    /** The event-info JSON file to edit; omitted to create an info.json in `parentFolderId`. */
    fileId?: string;
    /** The file's Drive version, when the caller has it from a listing — shares that cache entry. */
    fileVersion?: string;
    parentFolderId: string;
}

/** View/edit an event-info JSON file by id, or create an info.json in a folder that has none. */
export function EventInfoEditor({isOpen, onClose, fileId, fileVersion, parentFolderId}: EventInfoEditorProps) {
    const infoQuery = useJsonFile(fileId, parseEventInfoContentFile, {version: fileVersion});
    const saveInfo = useSaveEventInfo();
    const photographerSuggestions = usePhotographerSuggestions();
    const eventSuggestions = useEventSuggestions();

    const [title, setTitle] = useState('');
    const [cover, setCover] = useState('');     // the '400/' prefix is stripped for editing, but added back when saving
    const [photographers, setPhotographers] = useState<string[]>([]);
    const [events, setEvents] = useState<string[]>([]);
    const [hasLoadedForm, setHasLoadedForm] = useState(false);
    const [beautify, setBeautify] = useState(true);
    /** The content the form was loaded with, `null` when creating a file. */
    const [initial, setInitial] = useState<JsonFileContent | null>(null);

    useEffect(() => {
        if (!isOpen) {
            setHasLoadedForm(false);
            return;
        }
        // Creating a file has nothing to load (its query is disabled and never succeeds).
        if (!hasLoadedForm && (!fileId || infoQuery.isSuccess)) {
            const loaded = fileId ? infoQuery.data : null;
            const data = loaded?.info;
            setTitle(data?.title ?? '');
            setCover(getCoverFileName(data?.cover) ?? '');
            setPhotographers(data?.photographers ?? []);
            setEvents(data?.events ?? []);
            setInitial(loaded?.content ?? null);
            setHasLoadedForm(true);
        }
    }, [isOpen, hasLoadedForm, fileId, infoQuery.isSuccess, infoQuery.data]);

    async function handleSave() {
        const info = normalizeEventInfo({title, cover: writeCoverFileName(cover), photographers, events});

        // Nothing changed: saving would only create a new Drive version of the same content.
        if (initial && isSameEventInfo(initial, info, beautify)) {
            onClose();
            return;
        }

        try {
            await saveInfo.mutateAsync({fileId, parentFolderId, info, beautify});
            toaster.create({type: 'success', title: 'Mentve'});
            onClose();
        } catch (e) {
            toaster.create({type: 'error', title: describeDriveError(e)});
        }
    }

    // v4 keeps a disabled query (create mode) in `loading` status, so only a file being read counts.
    const isLoadingExisting = !hasLoadedForm && !!fileId && infoQuery.isLoading;
    const loadError = fileId && infoQuery.isError ? infoQuery.error : undefined;
    const hasInvalidFormat = !!fileId && infoQuery.isSuccess && infoQuery.data === null;

    return <Dialog.Root
        open={isOpen}
        onOpenChange={(e) => {
            if (!e.open) onClose();
        }}
        size="xl"
        preventScroll={false}
    >
        <Portal>
        <Dialog.Backdrop/>
        <Dialog.Positioner>
        <Dialog.Content>
            <Dialog.Header><Dialog.Title>{fileId ? 'Információ szerkesztése' : 'Információs fájl létrehozása'}</Dialog.Title></Dialog.Header>
            <DialogCloseButton/>
            <Dialog.Body>
                {isLoadingExisting
                    ? <Center py={10}><Spinner/></Center>
                    : loadError
                        ? <Alert.Root status="error"><Alert.Indicator/><Alert.Title>{describeDriveError(loadError)}</Alert.Title></Alert.Root>
                        : hasInvalidFormat
                            ? <Alert.Root status="error"><Alert.Indicator/><Alert.Title>{INVALID_EVENT_INFO_MESSAGE}</Alert.Title></Alert.Root>
                            : <VStack gap={4} align="stretch">
                        <Field.Root required invalid={title.length < 3}>
                            <Field.Label>Cím <Field.RequiredIndicator/></Field.Label>
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                _invalid={INVALID_INPUT}
                            />
                        </Field.Root>
                        <Field.Root invalid={cover !== '' && !isPictureFileName(cover)}>
                            <Field.Label>Borítókép</Field.Label>
                            <InputGroup startAddon="400/">
                                <Input
                                    value={cover}
                                    onChange={(e) => setCover(e.target.value)}
                                    _invalid={INVALID_INPUT}
                                    placeholder="pl. 20260114_235218_dave.jpg"
                                />
                            </InputGroup>
                        </Field.Root>
                        <Field.Root>
                            <Field.Label>Fotósok</Field.Label>
                            <TagListInput
                                value={photographers}
                                onChange={setPhotographers}
                                suggestions={photographerSuggestions}
                                placeholder="Fotós hozzáadása…"
                            />
                        </Field.Root>
                        <Field.Root>
                            <Field.Label>Rendezvénycímkék</Field.Label>
                            <TagListInput
                                value={events}
                                onChange={setEvents}
                                suggestions={eventSuggestions}
                                placeholder="Címke hozzáadása…"
                            />
                        </Field.Root>
                    </VStack>
                }
            </Dialog.Body>
            <Dialog.Footer gap={0}>
                <Checkbox.Root
                    mr="auto"
                    colorPalette="blue"
                    checked={beautify}
                    onCheckedChange={(e) => setBeautify(e.checked === true)}
                >
                    <Checkbox.HiddenInput/>
                    <Checkbox.Control/>
                    <Checkbox.Label>Szépített JSON</Checkbox.Label>
                </Checkbox.Root>
                <Button variant="ghost" mr={3} onClick={onClose}>Mégse</Button>
                <Button
                    colorPalette="blue"
                    onClick={handleSave}
                    loading={saveInfo.isPending}
                    disabled={!title.trim() || isLoadingExisting || !!loadError || hasInvalidFormat}
                >
                    Mentés
                </Button>
            </Dialog.Footer>
        </Dialog.Content>
        </Dialog.Positioner>
        </Portal>
    </Dialog.Root>;
}
